import api from '@/lib/api/axios'
import type { ListParams, PaginatedResponse } from '@/lib/api/types'

export interface RequestApproval {
    objectID: string | null
    requestID: string | null
    requestdatetime: string | null
    userName: string | null
    objectDescription: string | null
    noObjects: number | null
    iconFilename: string | null
    /** Present when the request asked for its versions to be scanned before copying to egress. */
    scan?: RecoveryScanState | null
}

/** Where a request's pre-recovery scans have got to. 'review' = finished, waiting to be accepted. */
export interface RecoveryScanState {
    state: 'scanning' | 'review' | 'accepted'
    scanning: number
    acceptedBy: { id: number; name: string | null } | null
    needsSecondPerson: boolean
}

export type RecoveryScanCategory = 'clear' | 'indicators' | 'detections' | 'failed' | 'not_scanned' | 'running'

export interface RecoveryScanSummary {
    requestId: string
    review: {
        accepted: boolean
        acceptedBy: { id: number; name: string | null } | null
        acceptedAt: string | null
        acceptReason: string | null
        needsSecondPerson: boolean
    }
    total: number
    counts: Record<RecoveryScanCategory, number>
    items: Array<{
        versionId: number
        objectId: string
        objectName: string
        versionType: string | null
        versionDatetime: string | null
        status: number
        changedByReview: boolean
        category: RecoveryScanCategory
    }>
    remaining: import('@/lib/utils/scan-estimate').ScanEstimate | null
    notSent: number
    canAccept: boolean
    needsReason: boolean
}

export const requestApprovalsApi = {
    list: (params?: ListParams) => {
        // Convert perPage to limit for backend compatibility
        const queryParams = params ? {
            ...params,
            limit: params.perPage || params.limit || 20,
            perPage: undefined,
        } : undefined
        // Remove undefined values
        if (queryParams) {
            Object.keys(queryParams).forEach(key => {
                if (queryParams[key as keyof typeof queryParams] === undefined) {
                    delete queryParams[key as keyof typeof queryParams]
                }
            })
        }
        return api.get<PaginatedResponse<RequestApproval>>('/api/request-approvals', { params: queryParams })
    },
    approve: (requestID: string) => {
        return api.post<{ success: boolean }>('/api/request-approvals/approve', { requestID })
    },
    delete: (requestID: string) => {
        return api.post<{ success: boolean }>('/api/request-approvals/delete', { requestID })
    },
    scanSummary: (requestID: string) =>
        api.get<{ data: RecoveryScanSummary }>(`/api/request-approvals/${encodeURIComponent(requestID)}/scan`),
    acceptScan: (requestID: string, body: { reason?: string; withoutUnsent?: boolean }) =>
        api.post<{ data: RecoveryScanSummary }>(`/api/request-approvals/${encodeURIComponent(requestID)}/scan/accept`, body),
}
